import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  getEmailSettings, setEmailMode, getGlobalEmailConfig, saveGlobalEmailConfig,
  getUserEmailConfig, saveUserEmailConfig, testEmailConfig,
  getEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate,
  getEmailHistory
} from "../lib/api";
import { 
  Mail, Settings, FileText, History, Plus, Edit, Trash2, Send, 
  CheckCircle, XCircle, Loader2, Globe, User, AlertCircle
} from "lucide-react";

const SMTP_PRESETS = {
  zoho: { host: "smtp.zoho.com", port: 587, name: "Zoho Mail" },
  gmail: { host: "smtp.gmail.com", port: 587, name: "Gmail / Google Workspace" },
  custom: { host: "", port: 587, name: "Personnalisé" }
};

export const EmailSettingsPage = () => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("config");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Settings
  const [useGlobalConfig, setUseGlobalConfig] = useState(true);
  const [globalConfig, setGlobalConfig] = useState(null);
  const [userConfig, setUserConfig] = useState(null);
  
  // Form state
  const [configForm, setConfigForm] = useState({
    provider: "zoho",
    smtpHost: "smtp.zoho.com",
    smtpPort: 587,
    email: "",
    password: "",
    senderName: "",
    isActive: true
  });
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    subject: "",
    body: "",
    description: ""
  });
  
  // History
  const [emailHistory, setEmailHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === "templates") {
      loadTemplates();
    } else if (activeTab === "history") {
      loadHistory();
    }
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await getEmailSettings();
      const data = response.data;
      
      setUseGlobalConfig(data.useGlobalConfig);
      setGlobalConfig(data.globalConfig);
      setUserConfig(data.userConfig);
      
      // Pré-remplir le formulaire
      const config = isAdmin ? data.globalConfig : data.userConfig;
      if (config) {
        setConfigForm({
          provider: config.provider || "zoho",
          smtpHost: config.smtpHost || "smtp.zoho.com",
          smtpPort: config.smtpPort || 587,
          email: config.email || "",
          password: "",
          senderName: config.senderName || "",
          isActive: config.isActive !== false
        });
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await getEmailTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      toast.error("Erreur lors du chargement des templates");
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await getEmailHistory({ limit: 100 });
      setEmailHistory(response.data.items || []);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleProviderChange = (provider) => {
    const preset = SMTP_PRESETS[provider];
    setConfigForm({
      ...configForm,
      provider,
      smtpHost: preset.host || configForm.smtpHost,
      smtpPort: preset.port
    });
  };

  const handleModeChange = async (useGlobal) => {
    try {
      await setEmailMode(useGlobal);
      setUseGlobalConfig(useGlobal);
      toast.success(`Mode email: ${useGlobal ? "Configuration globale" : "Par vendeur"}`);
    } catch (error) {
      toast.error("Erreur lors du changement de mode");
    }
  };

  const handleSaveConfig = async () => {
    if (!configForm.email || !configForm.password) {
      toast.error("Email et mot de passe requis");
      return;
    }

    try {
      setSaving(true);
      if (isAdmin && useGlobalConfig) {
        await saveGlobalEmailConfig(configForm);
        toast.success("Configuration globale sauvegardée");
      } else {
        await saveUserEmailConfig(configForm);
        toast.success("Configuration personnelle sauvegardée");
      }
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConfig = async () => {
    if (!configForm.email || !configForm.password) {
      toast.error("Email et mot de passe requis pour le test");
      return;
    }

    try {
      setTesting(true);
      await testEmailConfig(configForm);
      toast.success("Email de test envoyé ! Vérifiez votre boîte de réception.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec du test");
    } finally {
      setTesting(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error("Nom, sujet et contenu requis");
      return;
    }

    try {
      if (editingTemplate) {
        await updateEmailTemplate(editingTemplate.id, templateForm);
        toast.success("Template mis à jour");
      } else {
        await createEmailTemplate({ ...templateForm, isActive: true });
        toast.success("Template créé");
      }
      setTemplateDialog(false);
      setEditingTemplate(null);
      setTemplateForm({ name: "", subject: "", body: "", description: "" });
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm("Supprimer ce template ?")) return;
    try {
      await deleteEmailTemplate(id);
      toast.success("Template supprimé");
      loadTemplates();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      description: template.description || ""
    });
    setTemplateDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Configuration Email</h1>
          <p className="text-muted-foreground">Gérez vos paramètres d&apos;envoi d&apos;emails</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="config" className="gap-2">
            <Settings className="h-4 w-4" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            Historique
          </TabsTrigger>
        </TabsList>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-6">
          {/* Mode Selection (Admin Only) */}
          {isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Mode de configuration
                </CardTitle>
                <CardDescription>
                  Choisissez si tous les utilisateurs utilisent la même configuration ou si chaque vendeur configure son propre email
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${useGlobalConfig ? 'bg-primary/10' : 'bg-muted'}`}>
                      {useGlobalConfig ? <Globe className="h-5 w-5 text-primary" /> : <User className="h-5 w-5" />}
                    </div>
                    <div>
                      <p className="font-medium">
                        {useGlobalConfig ? "Configuration globale" : "Configuration par vendeur"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {useGlobalConfig 
                          ? "Un seul compte email pour toute l'équipe" 
                          : "Chaque vendeur utilise son propre email"}
                      </p>
                    </div>
                  </div>
                  <Switch 
                    checked={useGlobalConfig} 
                    onCheckedChange={handleModeChange}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* SMTP Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                {isAdmin && useGlobalConfig ? "Configuration SMTP globale" : "Ma configuration email"}
              </CardTitle>
              <CardDescription>
                {isAdmin && useGlobalConfig 
                  ? "Cette configuration sera utilisée par tous les utilisateurs"
                  : "Configurez votre compte email pour envoyer des messages"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Provider Selection */}
              <div className="space-y-2">
                <Label>Fournisseur</Label>
                <Select value={configForm.provider} onValueChange={handleProviderChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un fournisseur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zoho">Zoho Mail</SelectItem>
                    <SelectItem value="gmail">Gmail / Google Workspace</SelectItem>
                    <SelectItem value="custom">Personnalisé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* SMTP Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Serveur SMTP</Label>
                  <Input
                    value={configForm.smtpHost}
                    onChange={(e) => setConfigForm({ ...configForm, smtpHost: e.target.value })}
                    placeholder="smtp.exemple.com"
                    disabled={configForm.provider !== "custom"}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input
                    type="number"
                    value={configForm.smtpPort}
                    onChange={(e) => setConfigForm({ ...configForm, smtpPort: parseInt(e.target.value) })}
                    placeholder="587"
                  />
                </div>
              </div>

              {/* Credentials */}
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={configForm.email}
                  onChange={(e) => setConfigForm({ ...configForm, email: e.target.value })}
                  placeholder="votre@email.com"
                />
              </div>

              <div className="space-y-2">
                <Label>Mot de passe / Mot de passe d&apos;application</Label>
                <Input
                  type="password"
                  value={configForm.password}
                  onChange={(e) => setConfigForm({ ...configForm, password: e.target.value })}
                  placeholder="••••••••"
                />
                {configForm.provider === "gmail" && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Pour Gmail, utilisez un &quot;Mot de passe d&apos;application&quot; généré dans les paramètres de sécurité Google
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Nom d&apos;expéditeur (optionnel)</Label>
                <Input
                  value={configForm.senderName}
                  onChange={(e) => setConfigForm({ ...configForm, senderName: e.target.value })}
                  placeholder="Mon Entreprise"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button onClick={handleSaveConfig} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Sauvegarder
                </Button>
                <Button variant="outline" onClick={handleTestConfig} disabled={testing}>
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Tester la configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Templates d&apos;email</CardTitle>
                <CardDescription>Créez des modèles d&apos;emails réutilisables</CardDescription>
              </div>
              {isAdmin && (
                <Button onClick={() => { setEditingTemplate(null); setTemplateForm({ name: "", subject: "", body: "", description: "" }); setTemplateDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nouveau template
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun template créé</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {templates.map((template) => (
                    <div key={template.id} className="p-4 border rounded-lg flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="font-medium">{template.name}</p>
                        <p className="text-sm text-muted-foreground">Sujet: {template.subject}</p>
                        {template.description && (
                          <p className="text-xs text-muted-foreground">{template.description}</p>
                        )}
                      </div>
                      {isAdmin && (
                        <div className="flex gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openEditTemplate(template)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Variables disponibles */}
          <Card>
            <CardHeader>
              <CardTitle>Variables disponibles</CardTitle>
              <CardDescription>Utilisez ces variables dans vos templates, elles seront remplacées automatiquement</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">{"{{prenom}}"} - Prénom du lead</Badge>
                <Badge variant="secondary">{"{{nom}}"} - Nom complet du lead</Badge>
                <Badge variant="secondary">{"{{email}}"} - Email du lead</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Historique des emails</CardTitle>
              <CardDescription>Tous les emails envoyés depuis le CRM</CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : emailHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun email envoyé</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emailHistory.map((email) => (
                    <div key={email.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {email.status === "sent" ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            <span className="font-medium">{email.subject}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            À: {email.leadName} ({email.leadEmail})
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Par: {email.senderName} • {new Date(email.sentAt).toLocaleString('fr-FR')}
                          </p>
                          {email.errorMessage && (
                            <p className="text-xs text-red-500">{email.errorMessage}</p>
                          )}
                        </div>
                        <Badge variant={email.status === "sent" ? "default" : "destructive"}>
                          {email.status === "sent" ? "Envoyé" : "Échec"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Modifier le template" : "Nouveau template"}</DialogTitle>
            <DialogDescription>
              Créez un modèle d&apos;email réutilisable. Utilisez les variables pour personnaliser le contenu.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du template</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="Ex: Bienvenue nouveau lead"
              />
            </div>
            <div className="space-y-2">
              <Label>Sujet de l&apos;email</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="Ex: Bienvenue {{prenom}} !"
              />
            </div>
            <div className="space-y-2">
              <Label>Contenu (HTML)</Label>
              <Textarea
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                placeholder="<p>Bonjour {{prenom}},</p><p>Nous sommes ravis de vous accueillir...</p>"
                rows={8}
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optionnelle)</Label>
              <Input
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="Description interne du template"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveTemplate}>Sauvegarder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
